import { apiEnvelope } from '@galapagos/shared';
import { refreshDataSchema, type RefreshData } from '@galapagos/shared/api/auth';
import type { ZodType } from 'zod';
import { runAuthTeardown } from './auth-teardown';
import {
  clearSessionTokens,
  getAuthToken,
  getRefreshToken,
  replaceSessionTokensIfRefreshUnchanged,
  toSessionTokens,
} from './session';

// A response parser, typed by what it PRODUCES and deliberately unconstrained
// in what it accepts. `ZodType<T>` would pin the schema's input type to `T`
// too, which silently forbids any schema carrying a `.default()`, `.catch()`,
// or `.transform()` — exactly the schemas a server-owned wire shape needs so a
// field the server added later still parses on an older bundle (ADR-015). The
// input is whatever came off the network, so `unknown` is the honest bound.
type ResponseSchema<T> = ZodType<T, unknown>;

export type ApiErrorKind = 'network' | 'parse' | 'envelope' | 'http' | 'auth';
export type AuthFailureHandler = () => Promise<void> | void;

export interface ApiError {
  kind: ApiErrorKind;
  httpStatus?: number;
  code?: string;
  message: string;
  /** Structured context from the envelope error (e.g. retryAfterSeconds on 429). */
  details?: Record<string, unknown>;
}

export type ApiResult<T> = { ok: true; data: T } | { ok: false; error: ApiError };

export interface ApiOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /**
   * A non-member bearer for a public route (e.g. an employer-IdP id_token).
   * It replaces the member bearer entirely: the member token is never sent
   * alongside it, and a 401 is the route's own answer, never a session
   * refresh trigger.
   */
  externalBearer?: string;
}

function apiBaseUrl(): string {
  return process.env.NEXT_PUBLIC_API_URL ?? '';
}

let authFailureHandler: AuthFailureHandler | null = null;
let authFailurePromise: Promise<void> | null = null;

export function setApiAuthFailureHandler(handler: AuthFailureHandler | null): () => void {
  authFailureHandler = handler;
  return () => {
    if (authFailureHandler === handler) {
      authFailureHandler = null;
    }
  };
}

async function notifyAuthFailure(): Promise<boolean> {
  const handler = authFailureHandler;
  if (handler === null) return false;
  if (authFailurePromise === null) {
    authFailurePromise = Promise.resolve()
      .then(() => handler())
      .catch(() => {})
      .then(() => {
        authFailurePromise = null;
      });
  }
  await authFailurePromise;
  return true;
}

function getAcceptLanguage(): string {
  return typeof navigator !== 'undefined' ? (navigator.languages?.[0] ?? navigator.language) : 'en';
}

function getBrowserTimeZone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone && zone.length > 0 ? zone : null;
  } catch {
    return null;
  }
}

interface RequestArgs<T> {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  schema: ResponseSchema<T>;
  body?: unknown;
  options?: ApiOptions;
}

function buildHeaders(body: unknown, options: ApiOptions | undefined): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Accept-Language': getAcceptLanguage(),
    ...(options?.headers ?? {}),
  };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (options?.externalBearer !== undefined) {
    headers.Authorization = `Bearer ${options.externalBearer}`;
    return headers;
  }
  const token = getAuthToken();
  if (token !== null) {
    headers.Authorization = `Bearer ${token}`;
    // Only a member-authenticated request carries the observed zone — an
    // unauthenticated caller has no member row for it to attach to. An
    // unreadable zone sends no header at all: the server must not receive a
    // guessed zone it would persist as an observation.
    const zone = getBrowserTimeZone();
    if (zone) headers['X-Device-Timezone'] = zone;
  }
  return headers;
}

function isAbort(err: unknown, signal: AbortSignal | undefined): boolean {
  return (err instanceof Error && err.name === 'AbortError') || signal?.aborted === true;
}

function toNetworkError(err: unknown): ApiError {
  return {
    kind: 'network',
    message: err instanceof Error ? err.message : 'network error',
  };
}

async function parseJsonResponse(
  response: Response,
): Promise<{ ok: true; raw: unknown } | { ok: false; error: ApiError }> {
  try {
    return { ok: true, raw: await response.json() };
  } catch {
    return {
      ok: false,
      error: response.ok
        ? { kind: 'parse', httpStatus: response.status, message: 'invalid JSON body' }
        : { kind: 'http', httpStatus: response.status, message: `HTTP ${response.status}` },
    };
  }
}

function parseEnvelope<T>(
  raw: unknown,
  schema: ResponseSchema<T>,
  response: Response,
): ApiResult<T> {
  const parsed = apiEnvelope(schema).safeParse(raw);
  if (!parsed.success) {
    return {
      ok: false,
      error: response.ok
        ? { kind: 'parse', httpStatus: response.status, message: 'response shape mismatch' }
        : { kind: 'http', httpStatus: response.status, message: `HTTP ${response.status}` },
    };
  }
  const envelope = parsed.data;
  if (envelope.error !== null || envelope.data === null || !response.ok) {
    return {
      ok: false,
      error: {
        kind: 'envelope',
        httpStatus: response.status,
        code: envelope.error?.code ?? (response.ok ? 'empty_envelope' : `http_${response.status}`),
        message:
          envelope.error?.message ?? (response.ok ? 'empty envelope' : `HTTP ${response.status}`),
        ...(envelope.error?.details !== undefined ? { details: envelope.error.details } : {}),
      },
    };
  }
  // `apiEnvelope` has already validated this data, but parsing it through the
  // caller's schema once more retains the concrete generic output at the type
  // boundary without trusting a cast.
  const data = schema.safeParse(envelope.data);
  if (!data.success) {
    return {
      ok: false,
      error: { kind: 'parse', httpStatus: response.status, message: 'response shape mismatch' },
    };
  }
  return { ok: true, data: data.data };
}

export type MemberBearerRefreshOutcome =
  | { kind: 'refreshed'; token: string }
  | { kind: 'rejected' }
  | { kind: 'network'; message: string };

let refreshPromise: Promise<MemberBearerRefreshOutcome> | null = null;

/** Bound on the refresh round-trip: a hung refresh must fail as a network
 *  error instead of pinning `refreshPromise` (and every queued 401) forever. */
const REFRESH_TIMEOUT_MS = 10_000;

// The refresh call is deliberately not routed through request(): it must never
// carry the expired bearer or re-enter refresh on a 401.
async function performMemberBearerRefresh(): Promise<MemberBearerRefreshOutcome> {
  const refreshToken = getRefreshToken();
  if (refreshToken === null) return { kind: 'rejected' };

  const controller = new AbortController();
  const deadline = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl()}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Accept-Language': getAcceptLanguage(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
      signal: controller.signal,
    });
  } catch (error: unknown) {
    return { kind: 'network', message: toNetworkError(error).message };
  } finally {
    clearTimeout(deadline);
  }

  const json = await parseJsonResponse(response);
  if (!json.ok) return { kind: 'rejected' };
  const parsed = parseEnvelope<RefreshData>(json.raw, refreshDataSchema, response);
  if (!parsed.ok || !parsed.data.access_token.trim()) return { kind: 'rejected' };

  const persisted = replaceSessionTokensIfRefreshUnchanged(
    refreshToken,
    toSessionTokens(parsed.data),
  );
  if (!persisted) return { kind: 'rejected' };
  return { kind: 'refreshed', token: parsed.data.access_token };
}

/** One single-flight refresh shared by every concurrent 401. */
export function refreshMemberBearer(): Promise<MemberBearerRefreshOutcome> {
  if (refreshPromise === null) {
    refreshPromise = performMemberBearerRefresh().then(
      (outcome) => {
        refreshPromise = null;
        return outcome;
      },
      () => {
        refreshPromise = null;
        return rejectedRefreshOutcome();
      },
    );
  }
  return refreshPromise;
}

function rejectedRefreshOutcome(): MemberBearerRefreshOutcome {
  return { kind: 'rejected' };
}

// The session is dead. The one auth teardown (credentials, then every
// registered member-scoped store and the query cache) always runs here —
// single-flight, so N requests failing after the shared refresh clear once.
// The optional failure handler is notified afterwards as a UI hand-off
// (navigation); it never owns the cleanup.
async function failAuthSession(): Promise<{ ok: false; error: ApiError }> {
  await runAuthTeardown('api-401');
  await notifyAuthFailure();
  return { ok: false, error: { kind: 'auth', httpStatus: 401, message: 'unauthorized' } };
}

type AttemptOutcome =
  | { kind: 'response'; response: Response; authAttached: boolean }
  | { kind: 'error'; result: { ok: false; error: ApiError } };

async function attempt<T>(args: RequestArgs<T>): Promise<AttemptOutcome> {
  const { method, path, body, options } = args;
  const headers = buildHeaders(body, options);
  const init: RequestInit = {
    method,
    headers,
    signal: options?.signal,
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(`${apiBaseUrl()}${path}`, init);
    // An external bearer is not the member's credential, so its 401 is never
    // treated as an expired member session.
    return {
      kind: 'response',
      response,
      authAttached: options?.externalBearer === undefined && headers.Authorization !== undefined,
    };
  } catch (err: unknown) {
    if (isAbort(err, options?.signal)) throw err;
    return { kind: 'error', result: { ok: false, error: toNetworkError(err) } };
  }
}

async function parseResult<T>(
  response: Response,
  schema: ResponseSchema<T>,
): Promise<ApiResult<T>> {
  const json = await parseJsonResponse(response);
  if (!json.ok) return json;
  return parseEnvelope(json.raw, schema, response);
}

async function request<T>(args: RequestArgs<T>): Promise<ApiResult<T>> {
  const first = await attempt(args);
  if (first.kind === 'error') return first.result;

  // Session recovery covers two 401 shapes: a request that carried the
  // member's bearer (the token expired mid-session), and a tokenless request
  // made while a refresh token is held (the access token lives only in memory,
  // so a same-tab reload lands here — the first authenticated call after the
  // reload re-mints the bearer instead of failing unrecoverably). A 401 with
  // neither is a domain answer from an unauthenticated endpoint (e.g. an
  // incorrect one-time code) and falls through to normal parsing so the
  // server's error code and message reach the caller.
  const recoverable =
    first.response.status === 401 &&
    args.options?.externalBearer === undefined &&
    (first.authAttached || getRefreshToken() !== null);
  if (recoverable) {
    const outcome = await refreshMemberBearer();
    if (outcome.kind === 'network') {
      return { ok: false, error: { kind: 'network', message: outcome.message } };
    }
    if (outcome.kind === 'rejected') {
      if (first.authAttached) return failAuthSession();
      // The held refresh token is dead but the 401 answered a tokenless call,
      // which may be a domain denial: surface the server's answer and drop the
      // dead credential quietly so later 401s stop retrying it.
      clearSessionTokens();
      return parseResult(first.response, args.schema);
    }
    const second = await attempt(args);
    if (second.kind === 'error') return second.result;
    if (second.response.status === 401) {
      // A 401 that survives a freshly minted bearer is a policy answer, not a
      // credential problem. On the bearer-carrying path it is treated as
      // session death like the default mobile mode (no domain-denial consumers
      // yet); on the tokenless path the server's own answer is surfaced so an
      // unauthenticated endpoint's domain 401 keeps its code and message.
      if (first.authAttached) return failAuthSession();
    }
    return parseResult(second.response, args.schema);
  }

  return parseResult(first.response, args.schema);
}

export function apiGet<T>(
  path: string,
  schema: ResponseSchema<T>,
  options?: ApiOptions,
): Promise<ApiResult<T>> {
  return request({ method: 'GET', path, schema, options });
}

export function apiPost<T>(
  path: string,
  body: unknown,
  schema: ResponseSchema<T>,
  options?: ApiOptions,
): Promise<ApiResult<T>> {
  return request({ method: 'POST', path, schema, body, options });
}

export function apiPut<T>(
  path: string,
  body: unknown,
  schema: ResponseSchema<T>,
  options?: ApiOptions,
): Promise<ApiResult<T>> {
  return request({ method: 'PUT', path, schema, body, options });
}
